/**
 * Shared formatting for P&L change percentages.
 *
 * A change percentage always carries an explicit sign, including exact zero, so that
 * every value in a column of P&L figures starts with a sign character and the digits
 * line up. This also keeps the percentage consistent with the paired currency amount,
 * which is signed the same way (+$0.00 alongside +0.00%).
 *
 * Rates and proportions (win rate, % of account) are NOT change percentages — they
 * stay unsigned and must not use this helper.
 */
export const ZERO_PERCENT = '0.00%';

export function formatSignedPercent(value) {
  if (value === null || value === undefined) return ZERO_PERCENT;
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return ZERO_PERCENT;
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
}

export const ZERO_CURRENCY = '$0.00';

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Currency counterpart to formatSignedPercent, for P&L amounts shown beside one.
 * Exact zero is signed for the same reason: it keeps the amount and its percentage
 * on the same leading character, and the column beneath them aligned.
 *
 * Callers that show a price or balance rather than a change strip the sign.
 */
export function formatSignedCurrency(value) {
  if (value === null || value === undefined) return ZERO_CURRENCY;
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return ZERO_CURRENCY;
  return `${num >= 0 ? '+' : '-'}${currencyFmt.format(Math.abs(num))}`;
}

/** Price or balance. Same digits as a P&L amount, without a leading plus. */
export function formatBalanceCurrency(value) {
  return formatSignedCurrency(value).replace(/^\+/, '');
}

export default formatSignedPercent;
